import { useEffect, useId, useRef, useState } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { ConvexError } from 'convex/values'
import type { FunctionReturnType } from 'convex/server'
import { Link } from 'react-router-dom'
import { ArrowRight, Loader2 } from 'lucide-react'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

type RequestData = FunctionReturnType<typeof api.testChangeRequests.listForPrescription>
type TestChangeRequest = RequestData['requests'][number]
type Decision = 'approve' | 'reject'
type DecisionChannel = 'chat' | 'email' | 'prescription'

function formatDate(timestamp: number) {
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(timestamp)
}

function errorMessage(error: unknown) {
  if (error instanceof ConvexError && typeof error.data === 'string') return error.data
  return error instanceof Error ? error.message : 'Risposta non salvata. Riprova.'
}

const statusLabels: Record<TestChangeRequest['status'], string> = {
  pending: 'Approvazione richiesta',
  approved: 'Cambio approvato',
  rejected: 'Cambio rifiutato',
  stale: 'Richiesta superata',
}

const statusClasses: Record<TestChangeRequest['status'], string> = {
  pending: 'border-amber-200 bg-amber-50 text-amber-900',
  approved: 'border-green-200 bg-green-50 text-green-900',
  rejected: 'border-gray-200 bg-gray-50 text-gray-700',
  stale: 'border-gray-200 bg-gray-50 text-gray-700',
}

function needsAttention(request: TestChangeRequest) {
  return request.status === 'pending' ||
    (request.status === 'approved' && request.sync_status !== 'synced')
}

export function TestChangeRequestPanel({
  prescriptionId,
  requestId,
  suggestedDecision,
  channel = 'prescription',
  fromChat = false,
  className = '',
}: {
  prescriptionId: Id<'prescriptions'>
  requestId?: string | null
  suggestedDecision?: string | null
  channel?: DecisionChannel
  fromChat?: boolean
  className?: string
}) {
  const data = useQuery(api.testChangeRequests.listForPrescription, { prescription_id: prescriptionId })
  const headingId = useId()
  const sectionRef = useRef<HTMLElement>(null)
  const focusedRequest = useRef<string | null>(null)
  const [visibleRequestIds, setVisibleRequestIds] = useState<ReadonlySet<string>>(() => new Set())
  const loaded = data !== undefined

  useEffect(() => {
    if (!data) return
    const activeIds = data.requests.filter(needsAttention).map(request => request._id)
    setVisibleRequestIds(previous => activeIds.every(id => previous.has(id))
      ? previous : new Set([...previous, ...activeIds]))
  }, [data])

  useEffect(() => {
    if (!requestId || !loaded || fromChat || focusedRequest.current === requestId) return
    focusedRequest.current = requestId
    sectionRef.current?.scrollIntoView({ block: 'start' })
    sectionRef.current?.focus({ preventScroll: true })
  }, [requestId, loaded, fromChat])

  if (data === undefined) {
    return requestId ? <p role="status" className={className}>Caricamento richiesta di cambio prova…</p> : null
  }
  if (!requestId && data.requests.length === 0) return null

  // A link from an email always selects its exact request, including resolved requests.
  // It never falls back to a newer request and never submits a decision on navigation.
  const selected = requestId ? data.requests.find(request => request._id === requestId) : undefined
  // Keep a request visible after a decision or concurrent update so its result and focus survive.
  const isVisible = (request: TestChangeRequest) => needsAttention(request) || visibleRequestIds.has(request._id)
  const active = requestId ? selected ? [selected] : [] : data.requests.filter(isVisible)
  const history = requestId ? [] : data.requests.filter(request => !isVisible(request))
  const initialDecision = suggestedDecision === 'approve' || suggestedDecision === 'reject'
    ? suggestedDecision : undefined

  return (
    <section
      ref={sectionRef}
      id={fromChat ? undefined : 'test-change-request'}
      tabIndex={-1}
      aria-labelledby={headingId}
      className={`min-w-0 scroll-mt-6 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring ${className}`}
    >
      <h2 id={headingId} className="font-semibold">Cambio prova richiesto da PrimoLab</h2>
      {requestId && !selected && (
        <p role="alert" className="mt-3 text-amber-900">
          Questa richiesta non è disponibile per la prescrizione aperta. Controlla il collegamento ricevuto.
        </p>
      )}
      <div className="divide-y divide-gray-200">
        {active.map(request => (
          <RequestItem key={request._id} request={request} canDecide={data.can_decide}
            channel={channel} fromChat={fromChat} initialDecision={requestId ? initialDecision : undefined} />
        ))}
      </div>
      {history.length > 0 && (
        <details className="mt-3 border-t border-gray-200 pt-3">
          <summary className="cursor-pointer rounded-sm font-medium focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring">
            Storico cambi prova ({history.length})
          </summary>
          <div className="divide-y divide-gray-200">
            {history.map(request => (
              <RequestItem key={request._id} request={request} canDecide={data.can_decide}
                channel={channel} fromChat={fromChat} />
            ))}
          </div>
        </details>
      )}
    </section>
  )
}

function RequestItem({ request, canDecide, channel, fromChat, initialDecision }: {
  request: TestChangeRequest
  canDecide: boolean
  channel: DecisionChannel
  fromChat: boolean
  initialDecision?: Decision
}) {
  const decide = useMutation(api.testChangeRequests.decide)
  const retrySync = useMutation(api.testChangeRequests.retrySync)
  const [decision, setDecision] = useState<Decision | null>(initialDecision ?? null)
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const inFlight = useRef(false)
  const articleRef = useRef<HTMLElement>(null)
  const noteRef = useRef<HTMLTextAreaElement>(null)
  const approveButtonRef = useRef<HTMLButtonElement>(null)
  const rejectButtonRef = useRef<HTMLButtonElement>(null)
  const previousFormVisible = useRef(false)
  const lastChosenDecision = useRef<Decision>(initialDecision ?? 'approve')
  const noteId = useId()
  const isPending = request.status === 'pending'
  const syncing = request.sync_status === 'pending' || request.sync_status === 'syncing'
  const formVisible = Boolean(decision && isPending && canDecide)

  useEffect(() => {
    if (formVisible && !previousFormVisible.current) noteRef.current?.focus()
    if (!formVisible && previousFormVisible.current) {
      const actionRef = lastChosenDecision.current === 'approve' ? approveButtonRef : rejectButtonRef
      const focusTarget = actionRef.current ?? articleRef.current
      focusTarget?.focus({ preventScroll: true })
    }
    previousFormVisible.current = formVisible
  }, [formVisible])

  const chooseDecision = (choice: Decision) => {
    lastChosenDecision.current = choice
    setDecision(choice)
    setNotice(null)
    setError(null)
  }

  const submitDecision = async () => {
    if (!decision || !isPending || !canDecide || inFlight.current) return
    inFlight.current = true
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      const result = await decide({
        request_id: request._id,
        decision,
        decision_note: note.trim() || undefined,
        channel,
      })
      setNotice(result.status === 'stale'
        ? 'La prova è cambiata: questa richiesta non può più essere approvata.'
        : result.duplicate
          ? 'La risposta era già stata registrata. Qui trovi lo stato aggiornato.'
          : result.status === 'approved'
            ? 'Approvazione salvata. Il cambio verrà applicato con la sincronizzazione a PrimoUp.'
            : 'Rifiuto salvato. La prova rimane invariata.')
      setDecision(null)
      setNote('')
    } catch (cause) {
      setError(errorMessage(cause))
    } finally {
      inFlight.current = false
      setSaving(false)
    }
  }

  const retry = async () => {
    if (inFlight.current) return
    inFlight.current = true
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      await retrySync({ request_id: request._id })
      setNotice('Nuovo tentativo di sincronizzazione avviato. L’approvazione è già salvata.')
      articleRef.current?.focus({ preventScroll: true })
    } catch (cause) {
      setError(errorMessage(cause))
    } finally {
      inFlight.current = false
      setSaving(false)
    }
  }

  return (
    <article ref={articleRef} tabIndex={-1} className="min-w-0 space-y-3 rounded-sm py-4 last:pb-0 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring">
      <div role="status" aria-atomic="true" className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className={statusClasses[request.status]}>{statusLabels[request.status]}</Badge>
        <span className="text-xs text-gray-600">{formatDate(request.created_at)}</span>
      </div>
      <dl className="grid min-w-0 gap-3 sm:grid-cols-2">
        <div className="min-w-0">
          <dt className="text-xs text-gray-600">Prova attuale nella richiesta</dt>
          <dd className="mt-1 break-words font-medium">{request.current_test_name} <span className="font-normal text-gray-600">(ID {request.current_test_id})</span></dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-gray-600">Prova proposta</dt>
          <dd className="mt-1 break-words font-medium">{request.target_test_name} <span className="font-normal text-gray-600">(ID {request.target_test_id})</span></dd>
        </div>
      </dl>
      <div>
        <p className="text-xs text-gray-600">Note del laboratorio</p>
        <p className="mt-1 whitespace-pre-wrap break-words">{request.notes || 'Nessuna nota.'}</p>
      </div>

      {request.status === 'stale' && <p className="text-gray-700">La prova della prescrizione è cambiata. Questa richiesta è superata e non può essere approvata.</p>}
      {request.decided_at && (
        <p className="text-xs text-gray-600">Risposta registrata{request.decided_by_name ? ` da ${request.decided_by_name}` : ''} il {formatDate(request.decided_at)}.</p>
      )}
      {request.decision_note && <p className="whitespace-pre-wrap break-words"><span className="font-medium">Nota di risposta: </span>{request.decision_note}</p>}
      {request.status === 'approved' && syncing && (
        <p role="status" className="flex items-start gap-2 text-gray-700"><Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden="true" />Approvazione salvata. Cambio prova in sincronizzazione con PrimoUp.</p>
      )}
      {request.status === 'approved' && request.sync_status === 'synced' && <p className="text-green-800">Cambio prova applicato e sincronizzato con PrimoUp.</p>}
      {request.status === 'approved' && request.sync_status === 'failed' && (
        <div className="space-y-2 text-red-800">
          <p role="alert">Approvazione salvata, ma il cambio non è stato sincronizzato con PrimoUp.</p>
          {request.sync_error && <p className="whitespace-pre-wrap break-words">{request.sync_error}</p>}
          {canDecide ? <Button type="button" variant="outline" disabled={saving} className="h-auto min-h-11 whitespace-normal" onClick={() => void retry()}>{saving ? 'Invio in corso…' : 'Riprova sincronizzazione'}</Button>
            : <p>Il titolare della clinica può riprovare la sincronizzazione.</p>}
        </div>
      )}
      {isPending && !canDecide && <p className="text-gray-700">È richiesta la risposta del titolare della clinica.</p>}
      {isPending && canDecide && (decision ? (
        <form className="space-y-3 border-t border-gray-200 pt-3" aria-label="Conferma risposta al cambio prova" onSubmit={event => { event.preventDefault(); void submitDecision() }}>
          <p className="font-medium">{decision === 'approve' ? 'Conferma l’approvazione del cambio prova' : 'Conferma il rifiuto del cambio prova'}</p>
          <p className="text-gray-700">{decision === 'approve'
            ? 'La prova proposta sarà inviata a PrimoUp. La risposta sarà registrata nella prescrizione, in chat e nella segnalazione.'
            : 'La prova attuale rimarrà invariata. Il rifiuto sarà registrato nella prescrizione, in chat e nella segnalazione.'}</p>
          <div>
            <label htmlFor={noteId} className="mb-1 block font-medium">Nota di risposta <span className="font-normal text-gray-600">(facoltativa)</span></label>
            <textarea ref={noteRef} id={noteId} rows={3} maxLength={2000} value={note} disabled={saving}
              onChange={event => setNote(event.target.value)}
              className="w-full rounded-md border border-gray-500 bg-white px-3 py-2 text-base focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={saving} className="h-auto min-h-11 whitespace-normal text-gray-950">{saving ? 'Salvataggio…' : decision === 'approve' ? 'Conferma approvazione' : 'Conferma rifiuto'}</Button>
            <Button type="button" variant="outline" disabled={saving} className="min-h-11" onClick={() => { setDecision(null); setError(null) }}>Annulla</Button>
          </div>
        </form>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button ref={approveButtonRef} type="button" className="min-h-11 text-gray-950" disabled={saving} onClick={() => chooseDecision('approve')}>Approva cambio</Button>
          <Button ref={rejectButtonRef} type="button" variant="outline" className="min-h-11" disabled={saving} onClick={() => chooseDecision('reject')}>Rifiuta cambio</Button>
        </div>
      ))}
      {error && <p role="alert" className="whitespace-pre-wrap break-words text-red-800">{error}</p>}
      {notice && <p role="status" className="text-gray-700">{notice}</p>}
      {fromChat && <Link to={`/prescriptions/${request.prescription_id}?testChange=${request._id}#test-change-request`} className="inline-flex min-h-11 items-center gap-2 rounded-sm font-medium underline underline-offset-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring">Apri richiesta nella prescrizione <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" /></Link>}
    </article>
  )
}
